import React, { useEffect, useState, useMemo } from "react";
import OrderSummaryPage from "../../components/CustomerOrdersList";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetOrderByIdQuery,
  useUpdateOrderStatusMutation,
} from "../../api/api";
import { useToast } from "../../@/components/ui/use-toast";
import { setAppState } from "../../redux/slices/appState";
import { useDispatch } from "react-redux";
import { Button } from "../../@/components/ui/button";
import {
  CLICK_UP_TO_SCHEDULE_DELIVERY_HOURS,
  ModeLookup,
  STATUS,
  USER_ROLE,
} from "../../lib/constants";
import { useSelector } from "react-redux";
import {
  determineOrderStatus,
  downloadPdfFile,
  formatScheduleDateTime,
  IsServiceTypeVendor,
  tokenDecoder,
} from "../../lib/utils";
import OrderStepper from "./OrderStepper";
import OrderCancelDialog from "./OrderCancelDialog";
import Invoice from "../../components/common/OrderInvoice";
import { TbCalendarClock } from "react-icons/tb";
import { LuUserSquare2 } from "react-icons/lu";
import {
  MdOutlineLocationOn,
  MdOutlineMail,
  MdOutlinePayment,
  MdOutlineLocalPhone,
  MdOutlineDateRange,
} from "react-icons/md";
import { IoTimeOutline } from "react-icons/io5";
import moment from "moment";
import SortupDialog from "../../components/common/SortupDialog";
import PrintPage from "../../components/PrintPage";
import OrderLabeldata from "./OrderLabeldata";

const OrdersForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();
  const dispatch = useDispatch();
  const token = useSelector((state: any) => state.auth.token);
  const decodedToken = tokenDecoder(token);
  const isNotEditable = decodedToken.role === "VENDOR" ? false : true;
  const serviceTypeId = decodedToken.serviceTypeId;
  const role = decodedToken.role;
  const isServiceTypeVendor = IsServiceTypeVendor();
  const [showDispatchConfirm, setShowDispatchConfirm] = useState(false);
  const { ...orders } = useGetOrderByIdQuery({
    orderId: parseInt(id as string),
  });
  const [currentStep, setCurrentStep] = useState(1);
  const [cancelResone, setCancelResone] = useState("");
  const [selectedModeId, setSelectedModeId] = useState(null);

  useEffect(() => {
    if (!orders.isLoading && orders?.data?.data) {
      const statusId = orders.data.data.statusId;
      switch (statusId) {
        case STATUS.OrderActivated:
          setCurrentStep(1);
          break;
        case STATUS.OrderConfirmed:
          setCurrentStep(2);
          break;
        case STATUS.OrderDispatched:
          setCurrentStep(3);
          break;
        case STATUS.OrderCancelled:
          setCurrentStep(6);
          break;
        case STATUS.OrderDelivered:
          setCurrentStep(4);
          break;
        case STATUS.OrderCompleted:
          setCurrentStep(4);
          break;
        case STATUS.OrderReturned:
          setCurrentStep(5);
          break;
        default:
          setCurrentStep(1);
      }
      setSelectedModeId(orders.data.data.orderItems[0]?.modeId);
    } else if (orders.error) {
      toast({
        title: "Error!",
        description: "Something went wrong!",
      });
      setTimeout(() => {
        window.location.href = "/orders";
      }, 1000);
    }
  }, [orders.isLoading, orders?.data]);

  const [updateOrderStatus, { isLoading: statusUpdating }] =
    useUpdateOrderStatusMutation();
  const [vendorNote, setVendorNote] = useState(
    orders.data?.data?.vendorNote || ""
  );
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setVendorNote(e.target.value);
  };

  const handleOpenTrackingUrl = () => {
    const url = orders.data?.data.externalDeliveryTrackingUrl;
    if (url) {
      window.open(url, "_blank"); // Open in a new tab
    } else {
      toast({
        title: "Error!",
        description: "Tracking URL not available.",
      });
    }
  };

  const handleDownloadButton = async () => {
    if (!orders) return;
    const {
      customerName,
      deliveryAddress,
      orderItems,
      serviceFee,
      paymentProcessingFee,
      customerNote,
      vendorNote,
      totalAmount,
      deliveryCharges,
      customerTotalAmount,
      oldTotalAmountCustomer,
      oldTotalAmount,
      deliveryNumber,
      oldDeliveryCharges,
    } = orders?.data?.data;

    downloadPdfFile(
      <Invoice
        id={id}
        customerName={customerName}
        customerPhoneNumber={deliveryNumber}
        deliveryAddress={deliveryAddress}
        orderItems={orderItems}
        serviceFee={serviceFee}
        paymentProcessingFee={paymentProcessingFee}
        totalAmount={
          decodedToken.role === USER_ROLE.VENDOR
            ? totalAmount
            : customerTotalAmount
        }
        customerNote={customerNote}
        vendorNote={vendorNote}
        userRole={decodedToken.role}
        deliveryFee={deliveryCharges}
        oldDeliveryFee={oldDeliveryCharges}
        oldTotalAmount={
          decodedToken.role === USER_ROLE.VENDOR
            ? oldTotalAmount
            : oldTotalAmountCustomer
        }
        isShowTransactionFee={true}
        isShowDeliveryFee={
          decodedToken.role === USER_ROLE.VENDOR ? false : true
        }
      />,
      `Order_${id}_Receipt.pdf`
    );
  };

  const handleSave = async () => {
    if (currentStep === 3) {
      const scheduleDropoff = orders?.data?.data?.scheduleDropoffDateTime;
      if (scheduleDropoff) {
        const scheduleDate = moment(scheduleDropoff).startOf("day");
        const today = moment().startOf("day");

        if (scheduleDate.isAfter(today)) {
          setShowDispatchConfirm(true);
          return;
        }
      }
    }
    await processSave();
  };
  const processSave = async () => {
    const orderStatus = determineOrderStatus(
      currentStep,
      serviceTypeId,
      orders
    );
    try {
      const response = await updateOrderStatus({
        orderId: parseInt(id as string),
        body: {
          statusId: cancelResone !== "" ? STATUS.OrderCancelled : orderStatus,
          vendorNote: vendorNote !== "" ? vendorNote : undefined,
          cancellationReason: cancelResone !== "" ? cancelResone : undefined,
          bookedEventId: orders?.data?.data?.bookedVendorProductTimeSlot?.id
            ? orders?.data?.data?.bookedVendorProductTimeSlot?.id
            : undefined,
          calenderEventId: orders?.data?.data?.bookedVendorProductTimeSlot
            ?.calenderEventId
            ? orders?.data?.data?.bookedVendorProductTimeSlot?.calenderEventId
            : undefined,
        },
      }).unwrap();
      orders.refetch();
      toast({
        title: "Success!",
        description: `${response?.message}`,
      });
      dispatch(setAppState({ routeName: "orders" }));
      navigate(-1);
    } catch (err: any) {
      toast({
        title: "Error!",
        description: `${err?.message}`,
      });
    }
  };

  const handlePrintOrder = (OrderId: number) => {
    const printWindow = window.open(
      `/printPdf?orderId=${OrderId}`,
      "PrintWindow",
      "height=600,width=800,top=100,left=100,resizable=no,scrollbars=no,toolbar=no,menubar=no,location=no,status=no"
    );

    if (printWindow) {
      printWindow.focus();
    } else {
      alert("Popup blocked! Please allow popups for this website.");
    }
  };

  return (
    <div className="flex-1 flex-col w-full select-none">
      <SortupDialog
        isOpen={showDispatchConfirm}
        onClose={() => setShowDispatchConfirm(false)}
        header="Confirm Early Dispatch"
        footerButtons={[
          {
            name: "Yes, Dispatch Now",
            onClick: () => {
              setShowDispatchConfirm(false);
              processSave();
            },
            className: "text-white",
            disabled: false,
          },
          {
            name: "Cancel",
            onClick: () => setShowDispatchConfirm(false),
            className: "bg-white text-gray-900 border",
            disabled: false,
          },
        ]}
      >
        <p>
          This order is scheduled for{" "}
          {orders?.data?.data?.scheduleDropoffDateTime
            ? moment(orders.data.data.scheduleDropoffDateTime).format(
                "MMMM Do, YYYY"
              )
            : ""}
          . Are you sure you want to dispatch it today?
        </p>
      </SortupDialog>
      <div className="flex justify-between bg-customOffWhiteColor w-full py-3 px-4 md:px-6 space-y-3 md:space-y-0">
        <div className="w-full flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto">
            <Button
              variant="default"
              disabled={statusUpdating}
              className={`flex justify-center items-center bg-customGreenColor hover:bg-customGreenColor/90 text-white px-3 py-2 text-sm font-normal border focus:ring-0 rounded-lg w-full md:w-auto`}
              onClick={handleSave}
            >
              <img
                src="/Images/OrderFormImages/document-text.png"
                className="mr-1 text-lg w-5 h-5"
                alt="Save"
              />
              <p>{statusUpdating ? "Saving..." : "Save"}</p>
            </Button>

            <Button
              variant="outline"
              onClick={() => navigate(-1)}
              className="flex justify-center items-center text-customGrayText border-customGrayText px-3 py-2 text-sm font-normal border rounded-lg w-full md:w-auto"
            >
              <img
                src="/Images/ProductFormImages/close-square.png"
                className="w-5 h-5 mr-1"
                alt="Close"
              />
              Back
            </Button>
          </div>
          <div className="flex flex-col md:flex-row items-center gap-4 w-full md:w-auto">
            <OrderStepper
              perviousStep={orders?.data?.data?.statusId}
              currentStep={currentStep}
              setCurrentStep={setCurrentStep}
              role={decodedToken.role}
              serviceTypeId={serviceTypeId}
            />
            <OrderCancelDialog
              isNotEditable={isNotEditable}
              statusUpdating={statusUpdating}
              setCancelResone={setCancelResone}
              currentStep={currentStep}
              perviousStep={orders?.data?.data?.statusId}
              setCurrentStep={setCurrentStep}
            />
          </div>
        </div>
      </div>
      <div className="flex bg-grayBackgroundColor px-3 md:px-6 pt-2 pb-8 w-full min-h-screen">
        <div className="flex flex-col bg-white w-full h-full border rounded-lg shadow-xl">
          <div
            className={`grid  ${
              orders.data?.data?.statusId === STATUS.OrderCancelled
                ? "md:grid-cols-4"
                : "md:grid-cols-3"
            } grid-cols-1 w-full h-auto`}
            style={{ position: "relative" }}
          >
            <div
              className="border-b border-customBorderColor"
              style={{
                content: '""',
                position: "absolute",
                bottom: 0,
                left: 0,
                width: "97%",
                height: "1px",
              }}
            />
            <div className="col-span-1 flex flex-col py-8 px-6 md:text-start text-center w-auto">
              <p className="font-semibold text-lg text-customGrayDark">
                Contact Information
              </p>
              <div className="mt-2 font-normal text-sm text-customGrayDark">
                {orders.data?.data?.customerName && (
                  <p>
                    <span className="flex items-center gap-2">
                      <LuUserSquare2 className="text-lg" />
                      {orders.data?.data?.customerName}
                    </span>
                  </p>
                )}
                {orders.data?.data?.customerEmail && (
                  <p className="mt-2">
                    <span className="flex items-center gap-2">
                      <MdOutlineMail className="text-lg" />
                      <span
                        style={{
                          textDecoration: "none",
                          pointerEvents: "none",
                          color: "inherit",
                        }}
                        className="text-customGrayDark"
                      >
                        {orders.data?.data?.customerEmail
                          .split("")
                          .join("\u200B")}
                      </span>
                    </span>
                  </p>
                )}
                {orders.data?.data?.customerPhoneNumber && (
                  <p className="mt-2">
                    <span className="flex items-center gap-2">
                      <MdOutlineLocalPhone className="text-lg" />
                      <span
                        style={{
                          textDecoration: "none",
                          pointerEvents: "none",
                          color: "inherit",
                        }}
                        className="text-customGrayDark"
                      >
                        {orders.data?.data?.customerPhoneNumber
                          .split("")
                          .join("\u200B")}
                      </span>
                    </span>
                  </p>
                )}
              </div>
            </div>
            <div className="col-span-1 flex flex-col py-8 px-6 md:text-start text-center w-auto">
              <p className="font-semibold text-lg text-customGrayDark">
                {selectedModeId === ModeLookup.WALK_IN_SERVICE ||
                selectedModeId === ModeLookup.VIRTUAL
                  ? "Booking Details"
                  : "Shipping Info"}
              </p>
              <div className="font-normal text-sm text-customGrayDark">
                {orders.data?.data?.scheduleDropoffDateTime && (
                  <p className="mt-2">
                    <span className="flex items-center gap-2">
                      <TbCalendarClock className="text-lg" />
                      {`Delivery Time: ${formatScheduleDateTime(
                        orders.data?.data?.scheduleDropoffDateTime
                      )}`}
                    </span>
                  </p>
                )}
                {orders.data?.data?.pickupDateTime && (
                  <p className="mt-2">
                    <span className="flex items-center gap-2">
                      <TbCalendarClock className="text-lg" />
                      {`Pickup Time: ${formatScheduleDateTime(
                        orders.data?.data?.pickupDateTime,
                        CLICK_UP_TO_SCHEDULE_DELIVERY_HOURS
                      )}`}
                    </span>
                  </p>
                )}
                {orders.data?.data?.deliveryAddress && (
                  <p className="mt-2">
                    <span className="flex items-center gap-2">
                      {selectedModeId === ModeLookup.WALK_IN_SERVICE ||
                      selectedModeId === ModeLookup.VIRTUAL ? (
                        <MdOutlineDateRange
                          className="text-lg"
                          style={{ minWidth: "19px", minHeight: "19px" }}
                        />
                      ) : (
                        <MdOutlineLocationOn
                          className="text-lg font-bold"
                          style={{ minWidth: "19px", minHeight: "19px" }}
                        />
                      )}
                      {selectedModeId === ModeLookup.WALK_IN_SERVICE ||
                      selectedModeId === ModeLookup.VIRTUAL ? (
                        <p>
                          {moment(
                            orders.data?.data?.bookedVendorProductTimeSlot
                              ?.bookingDate
                          ).format("MMMM Do, YYYY")}
                        </p>
                      ) : (
                        <span
                          style={{
                            textDecoration: "none",
                            pointerEvents: "none",
                            color: "inherit",
                          }}
                          className="text-customGrayDark"
                        >
                          {orders.data?.data?.deliveryAddress
                            .split("")
                            .join("\u200B")}
                        </span>
                      )}
                    </span>
                  </p>
                )}
                {orders.data?.data?.bookedVendorProductTimeSlot?.startTime &&
                  (selectedModeId === ModeLookup.WALK_IN_SERVICE ||
                    selectedModeId === ModeLookup.VIRTUAL) && (
                    <p className="mt-2">
                      <span className="flex items-center gap-2">
                        <IoTimeOutline className="text-lg" />
                        {moment(
                          orders.data?.data?.bookedVendorProductTimeSlot
                            ?.startTime
                        ).format("h:mm A")}{" "}
                        -{" "}
                        {moment(
                          orders.data?.data?.bookedVendorProductTimeSlot
                            ?.endTime
                        ).format("h:mm A")}
                      </span>
                    </p>
                  )}
                {orders.data?.data?.deliveryNumber && (
                  <p className="mt-2">
                    <span className="flex items-center gap-2">
                      <MdOutlineLocalPhone className="text-lg" />
                      <span
                        style={{
                          textDecoration: "none",
                          pointerEvents: "none",
                          color: "inherit",
                        }}
                        className="text-customGrayDark"
                      >
                        {orders.data?.data?.deliveryNumber
                          .split("")
                          .join("\u200B")}
                      </span>
                    </span>
                  </p>
                )}
              </div>
            </div>
            <div className="col-span-1 flex flex-col py-8 px-6 md:text-start text-center w-auto">
              <p className="font-semibold text-lg text-customGrayDark">
                Payment Type
              </p>
              <div className="mt-2 font-normal text-sm text-customGrayDark">
                {orders.data?.data?.paymentMethod && (
                  <p>
                    <span className="flex items-center gap-2">
                      <MdOutlinePayment className="text-lg" />
                      {orders.data?.data?.paymentMethod}
                    </span>
                  </p>
                )}
              </div>
            </div>

            {orders.data?.data?.statusId === STATUS.OrderCancelled && (
              <div className="col-span-1 flex flex-col py-8 px-6 md:text-start text-center w-auto">
                {isServiceTypeVendor ? (
                  <p className="font-semibold text-lg text-customGrayDark">
                    Booking Cancelled
                  </p>
                ) : (
                  <p className="font-semibold text-lg text-customGrayDark">
                    Order Cancelled
                  </p>
                )}
                <div className="mt-2 font-normal text-sm text-customGrayDark">
                  {orders.data?.data?.cancellationReason && (
                    <p>Reason: {orders.data?.data?.cancellationReason}</p>
                  )}
                </div>
              </div>
            )}
          </div>
          {orders.data?.data?.shippingLabels && (
            <OrderLabeldata
              key={orders.data?.data?.shippingLabels}
              shippingLabels={orders.data?.data?.shippingLabels}
              data={orders.data?.data}
              orderApi={orders}
            />
          )}
          <div className="grid grid-cols-1 lg:grid-cols-3 bg-white w-full">
            <div className="lg:col-span-2 w-full">
              <OrderSummaryPage
                bookedVendorProductTimeSlot={
                  orders.data?.data?.bookedVendorProductTimeSlot
                }
                orders={orders.data?.data?.orderItems}
                deliveryFee={orders.data?.data?.deliveryCharges as number}
                totalAmount={orders.data?.data?.totalAmount as number}
                serviceFee={orders.data?.data?.serviceFee as number}
                paymentProcessingFee={
                  orders.data?.data?.paymentProcessingFee as number
                }
                oldTotalAmount={orders.data?.data?.oldTotalAmount as number}
                oldDeliveryCharges={
                  orders.data?.data?.oldDeliveryCharges as number
                }
              />
            </div>
            <div className="w-full p-4 md:p-7 space-y-4">
              <div className="w-full">
                <p className="text-customGrayBase font-semibold p-[0.85rem] text-sm bg-customTableBg">
                  Customer Note
                </p>
                <textarea
                  className={`w-full min-h-32 md:min-h-40 max-h-auto flex-grow border rounded-b p-2 resize-none focus:outline-none ${
                    !orders.data?.data?.customerNote
                      ? "text-center text-[12px]"
                      : "text-left text-[14px]"
                  }`}
                  value={orders.data?.data?.customerNote || ""}
                  readOnly
                  placeholder="No note provided"
                />
              </div>
              <div className="w-full">
                <p className="text-customGrayBase font-semibold p-[0.85rem] text-sm bg-customTableBg">
                  Vendor Note
                </p>
                <textarea
                  value={vendorNote || ""}
                  readOnly={isNotEditable}
                  onChange={handleChange}
                  className={`w-full min-h-32 md:min-h-40 max-h-auto flex-grow border rounded-b p-2 resize-none focus:outline-none ${
                    !vendorNote
                      ? "text-center text-[12px]"
                      : "text-left text-[14px]"
                  }`}
                  placeholder="No note provided"
                />
              </div>

              {!(decodedToken.role === "VENDOR" && isServiceTypeVendor) && (
                <div className="flex gap-2 flex-row flex-wrap">
                  <Button
                    onClick={handleDownloadButton}
                    className="flex justify-center items-center bg-customGreenColor text-white px-3 py-2 rounded-lg text-sm w-full md:w-auto"
                  >
                    {role === "ADMIN" ? "Admin Receipt" : "Vendor Receipt"}
                  </Button>
                  <Button
                    onClick={() => {
                      handlePrintOrder(orders.data?.data.id);
                    }}
                    className="flex justify-center items-center bg-customGreenColor text-white px-3 py-2 rounded-lg text-sm w-full md:w-auto"
                  >
                    Customer Receipt
                  </Button>
                  {orders.data?.data?.externalDeliveryTrackingUrl && (
                    <Button
                      onClick={handleOpenTrackingUrl}
                      className="flex justify-center items-center bg-customGreenColor text-white px-3 py-2 rounded-lg text-sm w-full md:w-auto"
                    >
                      Track Order
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(OrdersForm);
